import React, { useMemo } from 'react';
import { getBlobPath, STYLE_AXES } from '../utils/styleFieldLogic';

const StyleFieldChart = ({ radii, isSexy, isHip, targetCoord, identityDot, feasibilityMode }) => {
    const size = 320;
    const center = size / 2;
    const maxR = size / 2 - 20; // 20px padding

    // Memoize the blob path to avoid recalculation on every render
    const blobPath = useMemo(() => getBlobPath(radii, size), [radii, size]);

    // Calculate precise coordinates for drawing
    const getPoint = (angle, rValue) => {
        const r = (rValue / 100) * maxR;
        const rad = (angle * Math.PI) / 180;
        return {
            x: center + r * Math.cos(rad),
            y: center + r * Math.sin(rad)
        };
    };

    const hasTarget = Boolean(
        targetCoord
        && Number.isFinite(Number(targetCoord.angle))
        && Number.isFinite(Number(targetCoord.r))
    );
    const targetPoint = hasTarget ? getPoint(targetCoord.angle, targetCoord.r) : null;
    // Identity dot is pre-calculated x/y vector sum, needs scaling to canvas
    // Wait, identityDot from logic is (x,y) vector sum. 
    // We need to map it to canvas coordinates.
    // identityDot.x/y are roughly in [-1, 1] range * scaling.
    // Let's assume identityDot returned {x, y} already scaled relative to origin?
    // No, logic returned: x = sumX/8, y = sumY/8.
    // sumX is sum of R*cos. R is 0-100. So x is 0-100 scale avg.
    const identityPoint = {
        x: center + (identityDot.x / 100) * maxR * 3, // Boost factor 3 applied in logic? No, logic applied * 3 on 'r' but simple avg on x,y.
        // Wait, logic: r = sqrt... * 3. x = sumX/count.
        // If x is avg of 0-100, then (x/100)*maxR maps to pixels.
        // The boost factor in logic 'r' property was for magnitude, but x,y are components.
        // If we want to boost visual position, we multiply x,y.
        // Let's apply boost here for visibility: * 2.5
        y: center + (identityDot.y / 100) * maxR * 2.5
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow: 'visible' }}>
                <defs>
                    <radialGradient id="cloudGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                        <stop offset="0%" stopColor="rgba(59, 130, 246, 0.4)" />
                        <stop offset="100%" stopColor="rgba(147, 51, 234, 0.1)" />
                    </radialGradient>
                    <linearGradient id="pathGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.8" />
                        <stop offset="100%" stopColor="#fbbf24" stopOpacity="0.8" />
                    </linearGradient>
                    <filter id="glow">
                        <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
                        <feMerge>
                            <feMergeNode in="coloredBlur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                    <filter id="potentialGlow">
                        <feGaussianBlur stdDeviation="6" result="coloredBlur" />
                        <feMerge>
                            <feMergeNode in="coloredBlur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                {/* Background Circles */}
                {[25, 50, 75, 100].map(rProto => (
                    <circle
                        key={rProto}
                        cx={center}
                        cy={center}
                        r={(rProto / 100) * maxR}
                        fill="none"
                        stroke="#334155"
                        strokeWidth="1"
                        strokeDasharray="4 4"
                    />
                ))}

                {/* Axes Lines & Labels */}
                {STYLE_AXES.map((axis) => {
                    const p = getPoint(axis.angle, 100);
                    const labelP = getPoint(axis.angle, 115); // Label further out

                    return (
                        <g key={axis.id}>
                            <line
                                x1={center} y1={center}
                                x2={p.x} y2={p.y}
                                stroke="#334155"
                                strokeWidth="1"
                            />
                            <text
                                x={labelP.x}
                                y={labelP.y}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fill="#94a3b8"
                                fontSize="10"
                                fontWeight="bold"
                            >
                                {axis.label}
                            </text>
                        </g>
                    );
                })}

                {/* The Cloud */}
                <path
                    d={blobPath}
                    fill="url(#cloudGradient)"
                    stroke="#818cf8"
                    strokeWidth="2"
                    filter={feasibilityMode === 'POTENTIAL' ? "url(#potentialGlow)" : "url(#glow)"}
                    className="animate-pulse-slow"
                />

                {/* Special Type Labels */}
                {isSexy && (() => {
                    // Position 'Sexy' between Cool(135) and Artistic(90) -> ~112.5 deg
                    const r = (radii.cool + radii.artistic) / 2;
                    const p = getPoint(112.5, r + 15);
                    return (
                        <text x={p.x} y={p.y} fill="#f43f5e" fontSize="14" fontWeight="bold" textAnchor="middle" style={{ textShadow: '0 0 10px #f43f5e' }}>🔥 Sexy</text>
                    );
                })()}

                {isHip && (() => {
                    const p = getPoint(90, 110);
                    return (
                        <text x={p.x} y={p.y + 15} fill="#a855f7" fontSize="14" fontWeight="bold" textAnchor="middle" style={{ textShadow: '0 0 10px #a855f7' }}>⚡ Hip</text>
                    );
                })()}

                {/* Path Line */}
                {hasTarget ? (
                    <path
                        d={`M ${identityPoint.x} ${identityPoint.y} L ${targetPoint.x} ${targetPoint.y}`}
                        stroke="url(#pathGradient)"
                        strokeWidth="3"
                        strokeDasharray="6 6"
                        fill="none"
                        filter="url(#glow)"
                    />
                ) : null}

                {/* Identity Dot (You) */}
                <circle cx={identityPoint.x} cy={identityPoint.y} r="6" fill="#60a5fa" stroke="white" strokeWidth="2" />
                <text x={identityPoint.x} y={identityPoint.y - 10} fill="#60a5fa" fontSize="12" fontWeight="bold" textAnchor="middle">ME</text>

                {/* Target Dot (Chugumi) */}
                {hasTarget ? (
                    <>
                        <circle cx={targetPoint.x} cy={targetPoint.y} r="6" fill="#fbbf24" stroke="white" strokeWidth="2" />
                        <text x={targetPoint.x} y={targetPoint.y - 10} fill="#fbbf24" fontSize="12" fontWeight="bold" textAnchor="middle">GOAL</text>
                    </>
                ) : null}

            </svg>
        </div>
    );
};

export default StyleFieldChart;
